import React, { useState, useEffect } from "react";
import { Card } from "ui/card";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { Label } from "ui/label";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import { Search, Printer, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { getUserDetailsByITS } from "services/newRegistrationService";
import { maskEmail, maskMobileNumber, validatePhoneNumberInput } from "utils/commonutils";
import {
  Currency,
  CurrencyHead,
  getAllCurrencies,
  getCurrencyHeadList,
} from "services/currencyService";
import {
  getVenues,
  CreateReceiptPayload,
  createReceipts,
  getReceiptPdf,
} from "services/mannatNiyazService";
import { useAuth } from "contexts/AuthContext";
import axios from "axios";
import { FullScreenLoading } from "components/ui/loader";
import { useScreenPermission } from "hooks/useScreenPermission";
import { usePageHeader } from "contexts/PageHeaderContext";

// const isNiyazHead = (headName: string) =>
//   headName.toLowerCase().includes('niyaz');

const getHeadRate = (head: CurrencyHead, currency: string | ""): number => {
  if (!currency || !head?.currencies?.length) return 0;
  return (
    Number(
      head.currencies.find((c) => c.currencyCode === currency)?.unitRate ?? 0,
    ) || 0
  );
};

type CurrencyCode = "GBP" | "USD" | "INR" | "PKR" | "IRQ" | "KWT" | "AED";

interface HeadDetail {
  units: number;
  customAmount: number;
  discount: number;
  amount: number;
}

interface MumeenInfo {
  itsNo?: string;
  name: string;
  phone: string;
  email: string;
  watan: string;
}

interface ManualMumeenData {
  name: string;
  phone: string;
  email: string;
  watan: string;
  jamaat: string;
  jamiaat: string;
}

interface ReceiptDetail {
  headId: string;
  headName: string;
  units: number;
  unitRate: number;
  discount: number;
  amount: number;
  currency: CurrencyCode | string;
  isNiyaz: boolean;
}

interface ReceiptData {
  publicId?: string
  receiptNumber: string;
  receiptNo: string;
  location: string;
  itsNo: string;
  mumeenInfo: MumeenInfo & { itsNo: string; name: string };
  selectedHeads: string[];
  headDetails: Partial<Record<string, HeadDetail>>;
  currency?: CurrencyCode;
  details: ReceiptDetail[];
  head?: string;
  email?: string;
  total: number;
  approved: number;
  createdAt: string;
  userId: string;
  userName: string;
}

interface MannatNiyazReceiptPageProps {
  onNavigate?: (page: string) => void;
}

export interface VenueApi {
  venueId: number;
  venueName: string;
  contactNo: string;
  emailAddress: string;
  description: string;
  timeZone: string;
  isActive: boolean;
}

export function MannatNiyazReceiptPage({
  onNavigate,
}: MannatNiyazReceiptPageProps) {
  const { user } = useAuth();
  const { setPageHeader } = usePageHeader();
  const [loading, setLoading] = useState(false);
  const [isFetchingITS, setIsFetchingITS] = useState(false);
  const [ITSError, setITSError] = useState("");
  const [location, setLocation] = useState("");
  const [venues, setVenues] = useState<VenueApi[]>([]);
  const [itsNo, setItsNo] = useState("");
  const [mumeenInfo, setMumeenInfo] = useState<MumeenInfo | null>(null);
  const [isDataFromAPI, setIsDataFromAPI] = useState(false);
  const [manualMumeenData, setManualMumeenData] = useState<ManualMumeenData>({
    name: "",
    phone: "",
    email: "",
    watan: "",
    jamaat: "",
    jamiaat: "",
  });
  const [isPhoneEdited, setIsPhoneEdited] = useState(false);
  const [isEmailEdited, setIsEmailEdited] = useState(false);
  const [selectedHeads, setSelectedHeads] = useState<string[]>([]);
  const [currency, setCurrency] = useState("");
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currencyHeadRates, setCurrencyHeadRates] = useState<CurrencyHead[]>(
    [],
  );
  const [headDetails, setHeadDetails] = useState<
    Partial<Record<string, HeadDetail>>
  >({});
  const [formErrors, setFormErrors] = useState<{
    itsNo?: string;
    name?: string;
    phone?: string;
    email?: string;
    watan?: string;
    selectedHeads?: string;
    location?: string;
    currency?: string;
    totalAmount?: string;
  }>({});
  const [headErrors, setHeadErrors] = useState<
    Partial<Record<string, { units?: string; customAmount?: string }>>
  >({});
  const [receiptData, setReceiptData] = useState<ReceiptData | null>(null);

  const { screenName, icon: ScreenIcon} = useScreenPermission();

  useEffect(() => {
    setPageHeader({ 
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  // SET THE LAST SELECETED LOCATION
  useEffect(() => {
    const savedReceipts = localStorage.getItem("mannatNiyazReceipts");
    if (savedReceipts) {
      const receipts = JSON.parse(savedReceipts) as ReceiptData[];
      if (receipts.length > 0) {
        const latestReceipt = receipts[receipts.length - 1];
        setReceiptData(latestReceipt);
        setLocation(latestReceipt?.location || receiptData?.location || "");
      }
    }
  }, [receiptData?.location]);
  // SET THE LAST SELECETED LOCATION

  // FETCH CURRENCIES AND CITY DATA ON COMPONENT MOUNT
  // useEffect(() => {
  //   const fetchCurrencies = async () => {
  //     const data = await getAllCurrencies();
  //     setCurrencies(data);
  //   };
  //   const fetchVenues = async () => {
  //     const data = await getVenues();
  //     setVenues(data);
  //   };

  //   const fetchCurrencyHeadRates = async () => {
  //     const data = await getCurrencyHeadList({
  //       pageNo: 0,
  //       pageSize: 20,
  //     });

  //     const rawData = data as unknown;
  //     const items = Array.isArray(rawData)
  //       ? rawData
  //       : Array.isArray((rawData as { items?: unknown }).items)
  //         ? (rawData as { items: unknown[] }).items
  //         : [];

  //     const normalized = (items as Array<Record<string, unknown>>).map(
  //       (head) => ({
  //         ...head,
  //         isUnit: head?.isUnit ?? false,
  //         currencies: Array.isArray(head?.currencies)
  //           ? head.currencies
  //           : Array.isArray(head?.details)
  //             ? (head.details as Array<Record<string, unknown>>).map(
  //               (detail) => ({
  //                 publicId: String(
  //                   detail?.publicId ?? detail?.detailPublicId ?? "",
  //                 ),
  //                 id: String(
  //                   detail?.currencyHeadDetailId ?? detail?.currencyId ?? "",
  //                 ),
  //                 currencyId: Number(detail?.currencyId ?? 0),
  //                 currencyCode: String(
  //                   detail?.currencyCode ?? detail?.currencyName ?? "",
  //                 ),
  //                 currencySymbol: String(detail?.currencySymbol ?? ""),
  //                 unitRate: String(detail?.unitRate ?? detail?.unRate ?? ""),
  //               }),
  //             )
  //             : [],
  //       }),
  //     ) as CurrencyHead[];
  //     setCurrencyHeadRates(normalized);
  //   };
  //   fetchCurrencies();
  //   fetchVenues();
  //   fetchCurrencyHeadRates();
  // }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);

        const [currenciesData, venuesData, headRatesData] =
          await Promise.all([
            getAllCurrencies(),
            getVenues(),
            getCurrencyHeadList({
              pageNo: 0,
              pageSize: 20,
            }),
          ]);

        setCurrencies(currenciesData);
        setVenues(venuesData);

        const rawData = headRatesData as unknown;

        const items = Array.isArray(rawData)
          ? rawData
          : Array.isArray((rawData as { items?: unknown }).items)
            ? (rawData as { items: unknown[] }).items
            : [];

        const normalized = (items as Array<Record<string, unknown>>).map(
          (head) => ({
            ...head,
            isUnit: head?.isUnit ?? false,
            currencies: Array.isArray(head?.currencies)
              ? head.currencies
              : Array.isArray(head?.details)
                ? (head.details as Array<Record<string, unknown>>).map(
                    (detail) => ({
                      publicId: String(
                        detail?.publicId ?? detail?.detailPublicId ?? ""
                      ),
                      id: String(
                        detail?.currencyHeadDetailId ??
                          detail?.currencyId ??
                          ""
                      ),
                      currencyId: Number(detail?.currencyId ?? 0),
                      currencyCode: String(
                        detail?.currencyCode ??
                          detail?.currencyName ??
                          ""
                      ),
                      currencySymbol: String(
                        detail?.currencySymbol ?? ""
                      ),
                      unitRate: String(
                        detail?.unitRate ?? detail?.unRate ?? ""
                      ),
                    })
                  )
                : [],
          })
        ) as CurrencyHead[];

        setCurrencyHeadRates(normalized);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load page data");
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Toggle head selection
  const toggleHead = (headId: string) => {
    setSelectedHeads((prev) => {
      if (prev.includes(headId)) {
        const newDetails = { ...headDetails };
        delete newDetails[headId];
        setHeadDetails(newDetails);
        return prev.filter((id) => id !== headId);
      }

      const head = currencyHeadRates.find((item) => item.publicId === headId);
      if (!head) return prev;

      const defaultUnits = 1;
      const rate = getHeadRate(head, String(currency));
      // const amount = isNiyazHead(head.headName) ? 0 : rate * defaultUnits;
      const amount = rate * defaultUnits;

      // const rate = getHeadRate(head, currency);
      // const isManualAmount = rate <= 0;
      // const amount = isManualAmount
      //   ? 0
      //   : rate * defaultUnits;

      //       const isManualAmount =
      //   head.isUnit && rate <= 0;

      // const amount = isManualAmount
      //   ? 0
      //   : rate * defaultUnits;

      setHeadDetails((prevDetails) => ({
        ...prevDetails,
        [headId]: { units: defaultUnits, customAmount: 0, discount: 0, amount },
      }));
      return [...prev, headId];
    });
    setFormErrors((prev) => ({ ...prev, selectedHeads: "" }));
  };

  // Update head details
  const updateHeadDetails = (
    headId: string,
    field: "units" | "customAmount" | "discount",
    value: number,
  ) => {
    setHeadDetails((prev) => {
      const current = prev[headId] || {
        units: 1,
        customAmount: 0,
        discount: 0,
        amount: 0,
      };
      const head = currencyHeadRates.find((item) => item.publicId === headId);
      const rate = head ? getHeadRate(head, String(currency)) : 0;

      // let subtotal = 0;
      // if (isNiyazHead(head?.headName ?? '')) {
      //   subtotal = field === 'customAmount' ? value : current.customAmount;
      // } else {
      //   const units = field === 'units' ? value : current.units;
      //   subtotal = rate * units;
      // }

      const isManualAmount = rate <= 0;
      let subtotal = 0;
      if (isManualAmount) {
        subtotal = field === "customAmount" ? value : current.customAmount;
      } else {
        const units = field === "units" ? value : current.units;

        subtotal = rate * units;
      }

      const discount = field === "discount" ? value : current.discount;
      const newAmount = Math.max(0, subtotal - discount);

      return {
        ...prev,
        [headId]: {
          ...current,
          [field]: value,
          amount: newAmount,
        },
      };
    });
  };

  // Recalculate amounts when currency changes
  useEffect(() => {
    if (selectedHeads.length === 0) return;

    setHeadDetails((prev) => {
      let hasChange = false;
      const updatedDetails: Partial<Record<string, HeadDetail>> = { ...prev };

      selectedHeads.forEach((headId) => {
        const head = currencyHeadRates.find((item) => item.publicId === headId);
        if (!head) return;
        // if (isNiyazHead(head.headName)) return;

        const current = updatedDetails[headId];
        if (!current) return;

        const rate = getHeadRate(head, currency);
        const subtotal = rate * current.units;
        const amount = Math.max(0, subtotal - current.discount);

        if (current.amount !== amount) {
          updatedDetails[headId] = { ...current, amount };
          hasChange = true;
        }
      });

      return hasChange ? updatedDetails : prev;
    });
  }, [currency, selectedHeads, currencyHeadRates]);

  // Calculate total amount (already includes head-wise discounts)
  const totalAmount = selectedHeads.reduce((sum, headName) => {
    const details = headDetails[headName];
    return sum + (details?.amount || 0);
  }, 0);

  // const availableHeads = currency
  //   ? currencyHeadRates.filter(
  //     (head) =>
  //       // head.isUnit === true &&
  //       Array.isArray(head.currencies),
  //     // head.currencies.some((currencyEntry) => currencyEntry.currencyCode === currency)
  //   )
  //   : [];

  const availableHeads = currency ? currencyHeadRates.filter((head) => {
    // show all non-unit heads
    if (!head.isUnit) {
      return true;
    }
    // must have currencies array
    if (!Array.isArray(head.currencies)) {
      return false;
    }
    // find selected currency
    const currencyEntry = head.currencies.find(
      (entry) => entry.currencyCode === currency,
    );
    // show only if rate > 0
    return currencyEntry && Number(currencyEntry.unitRate) > 0;
  }) : [];



  // Handle form submission (common validation and save logic)
  const saveReceipt = async (emailSent: boolean) => {
    const errors: typeof formErrors = {};
    const newHeadErrors: typeof headErrors = {};

    if (!location.trim()) {
      errors.location = "Venue is required";
    }

    if (!currency.trim()) {
      errors.currency = "Currency is required";
    }

    if (!itsNo.trim()) {
      errors.itsNo = "ITS number is required";
    } else if (isDataFromAPI && !mumeenInfo && !manualMumeenData) {
      errors.itsNo = "Please enter a valid ITS number";
    }

    // if (!isDataFromAPI) {
    if (!manualMumeenData.name.trim()) {
      errors.name = "Name is required";
    }
    if (!manualMumeenData.phone.trim()) {
      errors.phone = "Phone is required";
    }
    if (!manualMumeenData.email.trim()) {
      errors.email = "Email is required";
    }
    if (!manualMumeenData.watan.trim()) {
      errors.watan = "Watan is required";
    }
    // }

    if (selectedHeads.length === 0) {
      errors.selectedHeads = "Please select at least one head";
    }

    for (const headName of selectedHeads) {
      const details = headDetails[headName];
      if (!details) {
        errors.selectedHeads = `Missing details for ${headName}`;
        continue;
      }
      if (headName === "Niyaz" && details.customAmount <= 0) {
        newHeadErrors[headName] = {
          ...(newHeadErrors[headName] || {}),
          customAmount: "Please enter a valid amount for Niyaz",
        };
      }
      if (headName !== "Niyaz" && details.units < 1) {
        newHeadErrors[headName] = {
          ...(newHeadErrors[headName] || {}),
          units: `Please enter valid units for ${headName}`,
        };
      }
    }

    if (totalAmount <= 0) {
      errors.totalAmount = "Total amount must be greater than zero";
    }

    setFormErrors(errors);
    setHeadErrors(newHeadErrors);

    if (
      Object.keys(errors).length > 0 ||
      Object.keys(newHeadErrors).length > 0
    ) {
      return null;
    }

    const finalMumeenInfo: MumeenInfo & { itsNo: string } = isDataFromAPI
      ? { ...(mumeenInfo as MumeenInfo), itsNo }
      : { itsNo, ...manualMumeenData };

    // const now = new Date();
    // const dateStr = now.toISOString().split("T")[0].replace(/-/g, "");

    const existingReceipts = JSON.parse(
      localStorage.getItem("mannatNiyazReceipts") || "[]",
    ) as ReceiptData[];

    // const todayLocationReceipts = existingReceipts.filter((r) =>
    //   r.receiptNumber.startsWith(`${dateStr}-${location}`),
    // );
    // const nextSequence = todayLocationReceipts.length + 1;
    // const receiptNumber = `${dateStr}-${location}-${String(nextSequence).padStart(2, "0")}`;
    const receiptNumber = `test-receipt-123`;

    const selectedVenue = venues.find((venue) => venue.venueName === location);
    const currencyItem = currencies.find(
      (curr) => curr.currencyCode === currency,
    );

    const receiptPayloadDetails = selectedHeads
      .map((headId) => {
        const head = currencyHeadRates.find((item) => item.publicId === headId);
        const details = headDetails[headId];
        if (!head || !details) return null;

        const matchedCurrency = head.currencies.find(
          (entry) => entry.currencyCode === currency,
        );
        const unitRate = matchedCurrency ? Number(matchedCurrency.unitRate) : 0;
        // const niyaz = isNiyazHead(head.headName);

        return {
          headId,
          headName: head.headName,
          // units: niyaz ? 0 : details.units,
          units: details.units,
          // unitRate: niyaz ? details.customAmount : unitRate,
          unitRate: details.customAmount || unitRate,
          discount: details.discount || 0,
          amount: details.amount || 0,
          currency: currency || currencyItem?.currencyCode || "",
          // isNiyaz: niyaz,
        };
      })
      .filter((detail): detail is ReceiptDetail => detail !== null);

    const receiptPayload: CreateReceiptPayload = {
      isEmailSent: emailSent,
      receiptNo: receiptNumber,
      blodUrl: "",
      itsNo,
      fullName: finalMumeenInfo.name ?? manualMumeenData.name,
      phoneNumber: finalMumeenInfo.phone ?? manualMumeenData.phone,
      email: finalMumeenInfo.email ?? manualMumeenData.email,
      watan: finalMumeenInfo.watan ?? manualMumeenData.watan,
      venueId: selectedVenue?.venueId ?? 0,
      currencyId: currencyItem?.currencyId ?? 0,
      totalAmount: totalAmount,
      isApproved: false,
      details: receiptPayloadDetails.map((detail) => ({
        unitRate: detail.unitRate,
        units: detail.units,
        roundoff: 0,
        isUnitRate: detail.isNiyaz ? false : true,
        currencyHeadMasterId: Number(
          currencyHeadRates.find((item) => item.publicId === detail.headId)
            ?.currencyHeadMasterId ??
          currencyHeadRates.find((item) => item.publicId === detail.headId)
            ?.id ??
          0,
        ),
        discount: detail.discount || 0,
      })),
      createdByUserID: Number(user?.id ?? localStorage.getItem("userId") ?? 0),
    };

    // Temporarily skip API call to test print view
    try {
      const result = await createReceipts(receiptPayload);
      //UPDATE RECEIPTNO FROM API RESPONSE
      receiptPayload.receiptNo = result?.receiptNo || "";
      receiptPayload.publicId = result?.publicId || ""

    } catch (error: unknown) {
      console.error("Receipt save failed", error);
      let message = "Failed to save receipt";
      if (axios.isAxiosError(error)) {
        message =
          error.response?.data?.message ||
          error.response?.data ||
          error.message ||
          message;
      } else if (error instanceof Error) {
        message = error.message;
      }
      toast.error(message);
      return null;
    }

    const receiptData: ReceiptData = {
      receiptNumber,
      receiptNo: receiptPayload?.receiptNo,
      publicId: receiptPayload?.publicId,
      email: receiptPayload?.email,
      location,
      itsNo,
      mumeenInfo: {
        ...finalMumeenInfo,
        name: finalMumeenInfo.name ?? manualMumeenData.name,
      },
      selectedHeads,
      headDetails,
      currency: (currency || currencyItem?.currencyCode) as CurrencyCode,
      details: receiptPayloadDetails,
      head: receiptPayloadDetails.map((detail) => detail.headName).join(", "),
      total: totalAmount,
      approved: 0,
      createdAt: new Date().toISOString(),
      userId: localStorage.getItem("userId") || "User1",
      userName: localStorage.getItem("userName") || "User1",
    };

    existingReceipts.push(receiptData);
    localStorage.setItem("mannatNiyazReceipts", JSON.stringify(existingReceipts),);
    setFormErrors({});
    setHeadErrors({});

    return receiptData;
  };


  // Reset form to initial state
  const handleReset = () => {
    setItsNo("");
    setMumeenInfo(null);
    setIsDataFromAPI(false);
    setManualMumeenData({
      name: "",
      phone: "",
      email: "",
      watan: "",
      jamaat: "",
      jamiaat: "",
    });
    setSelectedHeads([]);
    setHeadDetails({});
    setCurrency("");
    // setLocation('KRB');
  };


  // Handle Save & Print
  const handleSaveAndPrint = async (e: React.FormEvent) => {
    e.preventDefault();
    const receiptData = await saveReceipt(false);
    if (!receiptData) return;
    try {
      setLoading(true)
      const response = await getReceiptPdf(String(receiptData.publicId), false);
      const baseUrl = `${process.env.REACT_APP_MANNAT_NIYAZ_RECEIPT_BASE_URL}/${receiptData.publicId}/pdf?download=false`;

      // const blob = new Blob([response.data], { type: "application/pdf" });
      // const blobUrl = window.URL.createObjectURL(blob);
      // OPEN PDF
      // window.open(blobUrl, "_blank");

      if (response.status === 200) {
        window.open(baseUrl, "_blank");
      }
      // RESET FORM
      handleReset();
    } catch (error: unknown) {
      console.error(error);
      if (axios.isAxiosError(error) && error.response?.data) {
        const message = error.response.data.message || "Failed to open receipt PDF";
        toast.error(message);
      }
    } finally {
      setLoading(false)
    }

  };

  // Handle Save & Email
  const handleSaveAndEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const receiptData = await saveReceipt(true);
      if (!receiptData) return;
      toast.success(`Receipt ${receiptData?.receiptNo || receiptData.receiptNumber} created and emailed to ${receiptData?.email}!`,
        {
          description:
            "Email sent successfully",
          duration: 5000,
        }
      );
      handleReset();
    } catch (error: unknown) {
      console.error(error);
      if (
        axios.isAxiosError(error)
      ) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to save and email receipt";
        toast.error(message);
      } else {
        toast.error("Failed to save and email receipt");
      }
    } finally {
      setLoading(false);
    }
  };


  const handleFetchPartnerITS = async (itsNumber: string) => {
    if (itsNumber.length !== 8) {
      setITSError("Please enter a valid 8-digit ITS number");
      return;
    }

    setIsFetchingITS(true);
    setLoading(true);
    setITSError("");
    try {
      const itsDatas = await getUserDetailsByITS(itsNumber, true);
      if (!itsDatas?.mumin_Id && !itsDatas?.muminDataId) {
        toast.info("No record found for this ITS.");
        // if (!manualMumeenData) {
        // }
        setITSError("Please enter valid ITS number");
        setIsDataFromAPI(false);
        setManualMumeenData((prev) => ({
          ...prev,
          itsNumber: "",
          name: "",
          email: "",
          phone: "",
          watan: "",
          jamaat: "",
          jamiaat: "",
        }));
        return;
      } else {
        // Auto-populate partner fields from ITS data
        setIsDataFromAPI(true);
        setItsNo(itsDatas.mumin_Id || "");
        setManualMumeenData({
          ...manualMumeenData,
          name: `${itsDatas.first_Name} ${itsDatas.middle_Name} ${itsDatas.surname}`,
          email: itsDatas.email || manualMumeenData.email,
          phone: itsDatas.mobile_No || manualMumeenData.phone,
          watan: itsDatas?.vatan || manualMumeenData.watan,
          jamaat: itsDatas?.jamaat || manualMumeenData.jamaat,
          jamiaat: itsDatas?.jamiaat || manualMumeenData.jamiaat,
        });
        toast.success("ITS details fetched successfully");
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "ITS Number not found in the system. Please verify and try again.";
      setITSError(errorMessage || "ITS Number not found");
      toast.error(errorMessage || "Failed to fetch ITS details");
    } finally {
      setIsFetchingITS(false);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-20">
      {loading && <FullScreenLoading />}
      {/* <PageHeader
        title="Mannat & Niyaz Receipt"
        description="Create receipts for Zabihat, Mannat, and Niyaz offerings"
        icon={<FileText className="w-6 h-6" />}
      /> */}

      <div className="max-w-full" style={{ marginTop: "0px" }}>
        <form className="space-y-6">
          {/* Mumeen Information */}
          <Card className="p-4 bg-white/70 backdrop-blur-sm border-sky-200" style={{ gap: "4px", marginTop: "0px" }}>
            <div className="flex items-center gap-2">
              <Search className="h-5 w-5 text-sky-600" />
              <span className="font-medium text-sky-900">
                Mumeen Information
              </span>
            </div>

            <div className="space-y-4">
              <div>
                <InputFieldset
                  label="ITS Number"
                  required
                  id="itsNo"
                  value={itsNo}
                  placeholder="Enter 8-digit ITS number (auto-fetches data)"
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setItsNo(value);
                    setITSError("");
                    setFormErrors((prev) => ({ ...prev, itsNo: "" }));
                    // Auto-fetch when 8 digits are entered
                    if (value.length === 8) {
                      handleFetchPartnerITS(value); // ✅ pass value
                    }
                  }}
                  maxLength={8}
                  className="mt-1"
                  tabIndex={1}
                  disabled={isFetchingITS}
                />
                {isFetchingITS && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="w-4 h-4 animate-spin text-[#5B9BD5]" />
                  </div>
                )}
                {ITSError && <p className="text-xs text-red-600">{ITSError}</p>}
                {formErrors.itsNo && (
                  <p className="text-xs text-red-600">{formErrors.itsNo}</p>
                )}
                {isFetchingITS && (
                  <p className="text-xs text-[#5B9BD5]">
                    Fetching ITS details...
                  </p>
                )}
              </div>

              {/* Show data from API as read-only */}
              {/* {itsNo.length === 8 && isDataFromAPI && mumeenInfo && ( */}
              {itsNo.length === 8 && isDataFromAPI && manualMumeenData && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gradient-to-br from-sky-50 to-blue-50 rounded-lg border border-sky-300">
                  <div>
                    <InputFieldset
                      label="Name (Read-only)"
                      disabled
                      readOnly
                      value={manualMumeenData.name}
                      className="cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <InputFieldset
                      label="Phone"
                      value={
                        !isPhoneEdited && isDataFromAPI
                          ? maskMobileNumber(manualMumeenData.phone)
                          : manualMumeenData.phone
                      }
                      onChange={(e) => {
                        const value = validatePhoneNumberInput(e.target.value);
                        setIsPhoneEdited(true);
                        setManualMumeenData({
                          ...manualMumeenData,
                          phone: value,
                        });
                        setFormErrors((prev) => ({ ...prev, phone: "" }));
                      }}
                    />
                    {formErrors.phone && !manualMumeenData.phone && (
                      <p className="text-xs text-red-600 mt-1">
                        {formErrors.phone}
                      </p>
                    )}
                  </div>
                  <div>
                    <InputFieldset
                      label="Email"
                      value={
                        !isEmailEdited && isDataFromAPI
                          ? maskEmail(manualMumeenData.email)
                          : manualMumeenData.email
                      }
                      onChange={(e) => {
                        setIsEmailEdited(true);
                        setManualMumeenData({
                          ...manualMumeenData,
                          email: e.target.value,
                        });
                        setFormErrors((prev) => ({ ...prev, email: "" }));
                      }}
                    />
                    {formErrors.email && !manualMumeenData.email && (
                      <p className="text-xs text-red-600 mt-1">
                        {formErrors.email}
                      </p>
                    )}
                  </div>
                  <div>
                    <InputFieldset
                      label="Watan (Read-only)"
                      disabled
                      readOnly
                      value={manualMumeenData.watan}
                      className="cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <InputFieldset
                      label="Jamaat (Read-only)"
                      disabled
                      readOnly
                      value={manualMumeenData.jamaat}
                      className="cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <InputFieldset
                      label="Jamiaat (Read-only)"
                      disabled
                      readOnly
                      value={manualMumeenData.jamiaat}
                      className="cursor-not-allowed"
                    />
                  </div>
                </div>
              )}

              {/* Show editable fields when data is NOT from API */}
              {itsNo.length === 8 && !isDataFromAPI && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <InputFieldset
                      label="Name"
                      required
                      id="name"
                      value={manualMumeenData.name}
                      onChange={(e) => {
                        setManualMumeenData({
                          ...manualMumeenData,
                          name: e.target.value,
                        });
                        setFormErrors((prev) => ({ ...prev, name: "" }));
                      }}
                      placeholder="Enter full name"
                      tabIndex={2}
                    />
                    {formErrors.name && (
                      <p className="text-xs text-red-600 mt-1">
                        {formErrors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <InputFieldset
                      label="Phone"
                      required
                      id="phone"
                      value={manualMumeenData.phone}
                      onChange={(e) => {
                        const value = validatePhoneNumberInput(e.target.value);
                        setManualMumeenData({
                          ...manualMumeenData,
                          phone: value,
                        });
                        setFormErrors((prev) => ({ ...prev, phone: "" }));
                      }}
                      placeholder="Enter phone number"
                      tabIndex={2}
                    />
                    {formErrors.phone && (
                      <p className="text-xs text-red-600 mt-1">
                        {formErrors.phone}
                      </p>
                    )}
                  </div>
                  <div>
                    <InputFieldset
                      label="Email"
                      required
                      id="email"
                      value={manualMumeenData.email}
                      onChange={(e) => {
                        setManualMumeenData({
                          ...manualMumeenData,
                          email: e.target.value,
                        });
                        setFormErrors((prev) => ({ ...prev, email: "" }));
                      }}
                      placeholder="Enter email address"
                      tabIndex={2}
                    />
                    {formErrors.email && (
                      <p className="text-xs text-red-600 mt-1">
                        {formErrors.email}
                      </p>
                    )}
                  </div>
                  <div>
                    <InputFieldset
                      label="Watan"
                      required
                      id="watan"
                      value={manualMumeenData.watan}
                      onChange={(e) => {
                        setManualMumeenData({
                          ...manualMumeenData,
                          watan: e.target.value,
                        });
                        setFormErrors((prev) => ({ ...prev, watan: "" }));
                      }}
                      placeholder="Enter city, country"
                      tabIndex={2}
                    />
                    {formErrors.watan && (
                      <p className="text-xs text-red-600 mt-1">
                        {formErrors.watan}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Receipt Details */}
          <Card className="p-4 bg-white/70 backdrop-blur-sm border-sky-200" style={{ gap: "4px", marginTop: "10px" }}>
            <div className="">
              <span className="font-medium text-sky-900">Receipt Details</span>
            </div>

            <div className="space-y-5">
              {/* Location and Currency in One Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                <div>
                  <SelectFieldset label="Venues" required>
                    <Select
                      value={location}
                      onValueChange={(value) => {
                        setLocation(value);
                        setFormErrors((prev) => ({ ...prev, location: "" }));
                      }}
                    >
                      <SelectTriggerUI
                        id="location"
                        className="mt-1"
                        tabIndex={isDataFromAPI || itsNo.length !== 8 ? 2 : 6}
                      >
                        <SelectValue placeholder="Select Venue" />
                      </SelectTriggerUI>
                      <SelectContent>
                        {venues?.map((loc) => (
                          <SelectItem key={loc.venueId} value={loc.venueName}>
                            {loc.venueName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                  {formErrors.location && (
                    <p className="text-xs text-red-600 mt-1">
                      {formErrors.location}
                    </p>
                  )}
                </div>

                <div>
                  <SelectFieldset label="Currency" required>
                    <Select
                      value={currency}
                      onValueChange={(value) => {
                        setCurrency(value);
                        setSelectedHeads([]);
                        setHeadDetails({});
                        setFormErrors((prev) => ({
                          ...prev,
                          currency: "",
                          selectedHeads: "",
                        }));
                      }}
                    >
                      <SelectTriggerUI
                        id="currency"
                        className="mt-1"
                        tabIndex={isDataFromAPI || itsNo.length !== 8 ? 3 : 7}
                      >
                        <SelectValue placeholder="Select Currency" />
                      </SelectTriggerUI>
                      <SelectContent>
                        {currencies.map((curr) => (
                          <SelectItem
                            key={curr.currencyCode}
                            value={curr.currencyCode}
                          >
                            {curr.currencyCode} ({curr.currencySymbol})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                  {formErrors.currency && (
                    <p className="text-xs text-red-600 mt-1">
                      {formErrors.currency}
                    </p>
                  )}
                </div>
              </div>

              {/* Head Selection - Checkboxes for Multi-Select */}
              <div>
                <Label className="mt-5 mb-3 block text-sky-900">
                  Select Head(s) * (Multiple Selection Allowed)
                </Label>
                {/* button view start */}
                {!currency && (
                  <p className="text-sm text-gray-500">
                    Select a currency first to choose available heads.
                  </p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                  {currency && (
                    availableHeads.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No heads available for the selected currency.
                    </p>
                  ) : (
                    availableHeads.map((head, index) => {
                      const headId = head.publicId || "";
                      if (!headId) return null;
                      const rate = getHeadRate(head, String(currency));
                      // const displayValue = isNiyazHead(head.headName) ? "" : ` (${rate} ${currency})`;
                      // const displayValue = `${currency} ${rate}`;
                      const isSelected = selectedHeads.includes(headId);
                      return (
                        <label
                          key={headId}
                          htmlFor={headId}
                          className={`relative rounded-2xl border-2 cursor-pointer transition-all duration-300 flex flex-col items-center justify-center min-h-[100px] w-full sm:flex-1 sm:min-w-[200px] p-2 sm:p-4
                          ${isSelected ? ` bg-gradient-to-br from-sky-500 to-blue-600 border-sky-600 text-white shadow-lg`
                              : `bg-white border-sky-400 text-sky-900 hover:bg-blue-100 hover:border-red-300 hover:shadow-md`}`}>

                          {/* Hidden Checkbox */}
                          <input
                            type="checkbox"
                            id={headId}
                            checked={isSelected}
                            onChange={() => toggleHead(headId)}
                            className="hidden"
                            tabIndex={
                              isDataFromAPI || itsNo.length !== 8
                                ? 4 + index
                                : 8 + index
                            }
                          />

                          {/* Selected Tick */}
                          {isSelected && (
                            <div className="absolute top-1.5 right-1.5 w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full flex items-center justify-center">
                              <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 bg-green-500 rounded-full flex items-center justify-center">
                                <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            </div>
                          )}

                          {/* Head Name */}
                          <h3 className="text-sm sm:text-xl font-extrabold text-center">
                            {head.headName}
                          </h3>
                          {/* Unit Head */}
                          {head.isUnit && rate > 0 ? (
                            <>
                              <p className={`text-sm font-semibold ${isSelected ? "text-sky-100" : "text-sky-900"}`}>
                                <span className={`sm:text-lg font-bold ${isSelected ? "text-sky-100" : "text-sky-900"}`}>{rate} {currency}</span>  per unit
                              </p>
                            </>
                          ) : (
                            <p className={`sm:text-sm text-base font-medium ${isSelected ? "text-sky-100" : "text-sky-900"}`}>
                              Custom Amount
                            </p>
                          )}
                        </label>
                      );
                    })
                    )
                  )}
                </div>

                {formErrors.selectedHeads && (
                  <p className="text-xs text-red-600 mt-2">
                    {formErrors.selectedHeads}
                  </p>
                )}
                <div className="space-y-3 mt-4">
                  {selectedHeads.map((headId) => {
                    const head = currencyHeadRates.find(
                      (item) => item.publicId === headId,
                    );
                    if (!head) return null;

                    const details = headDetails[headId] || {
                      units: 1,
                      customAmount: 0,
                      discount: 0,
                      amount: 0,
                    };
                    // const rate = getHeadRate(head, String(currency));
                    const currencySymbol =
                      currencies.find((c) => c.currencyCode === currency)
                        ?.currencySymbol || currency;
                    const hasCurrencyDefinition =
                      getHeadRate(head, String(currency)) > 0;
                    // const niyaz = isNiyazHead(head.headName);
                    const isUnit = head.isUnit !== false;

                    return (
                      <Card
                        key={headId}
                        className="p-4 bg-gradient-to-br from-sky-50 to-blue-50 border-2 border-sky-300"
                      >
                        <div className="flex justify-between items-center flex-col md:flex-row gap-2 md:gap-4">
                          <div className="w-full md:w-1/8 mt-1">
                            <span className="text-base font-semibold text-sky-900">
                              {head.headName}
                            </span>
                          </div>

                          {!hasCurrencyDefinition ? (
                            <>
                              <div className="flex justify-between items-center gap-2 sm:block w-full md:w-1/4 mt-1">
                                <Label className="text-sky-700 text-xs whitespace-nowrap">
                                  Amount *
                                </Label>
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  value={details.customAmount || ""}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(
                                      /\D/g,
                                      "",
                                    );
                                    updateHeadDetails(
                                      headId,
                                      "customAmount",
                                      Number(value) || 0,
                                    );
                                  }}
                                  placeholder="Enter amount"
                                  className="w-[90px] sm:w-full mt-2 h-9 ext-sm appearance-none"
                                />
                                {headErrors[headId]?.customAmount && (
                                  <p className="text-xs text-red-600 mt-1">
                                    {headErrors[headId]?.customAmount}
                                  </p>
                                )}
                              </div>
                              <div
                                className="flex justify-between items-center gap-2
                                sm:block
                                w-full md:w-1/4 mt-1"
                              >
                                <Label className="hidden text-sky-700 text-xs whitespace-nowrap mb-2">
                                  Discount
                                </Label>
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  value={details.discount || ""}
                                  readOnly={!isUnit}
                                  disabled={!isUnit}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(
                                      /\D/g,
                                      "",
                                    );
                                    updateHeadDetails(
                                      headId,
                                      "discount",
                                      Number(value) || 0,
                                    );
                                  }}
                                  placeholder="0"
                                  className={`hidden w-[90px] sm:w-full h-9 px-2 py-2 text-sm ${!isUnit ? "cursor-not-allowed bg-slate-50" : ""}`}
                                />
                              </div>
                              <div className="sm:block flex justify-between w-full md:w-1/4">
                                <Label className="text-sky-700 text-xs whitespace-normal">
                                  Total
                                </Label>
                                <p className="text-lg mt-1 text-sky-900 font-semibold">
                                  {currencySymbol}{" "}
                                  {details.amount.toLocaleString()}
                                </p>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex  justify-between items-center gap-2 sm:block w-full md:w-1/4">
                                <Label className="text-sky-700 text-xs whitespace-nowrap mb-2">
                                  No. of Units *
                                </Label>
                                <div className="flex items-center border rounded-lg overflow-hidden w-fit">
                                  {/* Minus */}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateHeadDetails(
                                        headId,
                                        "units",
                                        Math.max(1, details.units - 1),
                                      )
                                    }
                                    className="px-3 py-1 bg-gray-50 border-r text-lg font-semibold"
                                  >
                                    -
                                  </button>

                                  {/* Value */}
                                  <div className="px-2 py-1 min-w-[50px] text-center font-medium">
                                    {details.units}
                                  </div>

                                  {/* Plus */}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateHeadDetails(
                                        headId,
                                        "units",
                                        details.units + 1,
                                      )
                                    }
                                    className="px-3 py-1 bg-gray-50 border-l text-lg font-semibold"
                                  >
                                    +
                                  </button>
                                </div>
                                {headErrors[headId]?.units && (
                                  <p className="text-xs text-red-600 mt-1">
                                    {headErrors[headId]?.units}
                                  </p>
                                )}
                              </div>
                              <div className="flex justify-between items-center gap-2 sm:block w-full md:w-1/4">
                                <Label className="text-sky-700 text-xs whitespace-nowrap mb-2">
                                  Discount
                                </Label>
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  value={details.discount || ""}
                                  readOnly={!isUnit}
                                  disabled={!isUnit}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(
                                      /\D/g,
                                      "",
                                    );
                                    updateHeadDetails(
                                      headId,
                                      "discount",
                                      Number(value) || 0,
                                    );
                                  }}
                                  placeholder="0"
                                  className={`w-[90px] sm:w-full h-9 px-2 py-2 text-sm ${!isUnit ? "cursor-not-allowed bg-slate-50" : ""}`}
                                />
                              </div>
                              <div className="sm:block flex justify-between w-full md:w-1/4">
                                <Label className="text-sky-700 text-xs whitespace-normal">
                                  Amount
                                </Label>
                                <p className="text-lg mt-1 text-sky-900 font-semibold">
                                  {currencySymbol}{" "}
                                  {details.amount.toLocaleString()}
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Final Total */}
              {selectedHeads.length > 0 && (
                <div className="p-5 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg border-2 border-green-400">
                  <div className="flex justify-between items-center gap-2">
                    <Label className="text-2xl text-green-800 text-sm">
                      Total Amount to Pay
                    </Label>
                    <p className="text-2xl mt-2 text-green-900 font-bold">
                      {
                        currencies.find((c) => c.currencyCode === currency)
                          ?.currencyCode
                      }{" "}
                      {totalAmount.toLocaleString()}
                    </p>
                  </div>
                  {formErrors.totalAmount && (
                    <p className="text-xs text-red-600 mt-2">
                      {formErrors.totalAmount}
                    </p>
                  )}
                </div>
              )}
            </div>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-end gap-4" style={{ marginTop: "10px" }}> 
            <Button
              type="button"
              onClick={handleSaveAndPrint}
              className="min-w-[180px] bg-sky-600 hover:bg-sky-700"
              tabIndex={isDataFromAPI || itsNo.length !== 8 ? 9 : 13}
            >
              <Printer className="h-4 w-4 mr-2" />
              Save & Print
            </Button>
            <Button
              type="button"
              onClick={handleSaveAndEmail}
              className="min-w-[180px] bg-green-400 hover:bg-green-700"
              tabIndex={isDataFromAPI || itsNo.length !== 8 ? 10 : 14}
            >
              <Mail className="h-4 w-4 mr-2" />
              Save & Email
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
